import React from 'react';
import {
    Button,
    Label,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@ai-content-hub/content-kit';

import {
    DEFAULT_SPEAKING_GRADING_QUESTION_TYPE,
    SPEAKING_GRADING_QUESTION_TYPES,
} from './schema';
import { SpeakingGradingExampleDialog } from './SpeakingGradingExampleDialog';

export interface SpeakingGradingTypeFieldProps {
    value?: string;
    onChange: (code: string) => void;
    disabled?: boolean;
    t: (key: string) => string;
}

/**
 * EAA-1857: lets the author pick which QT_SPEAK_* scorer grades this activity.
 *
 * `config.scoring.gradingQuestionType` has always driven
 * `/api/v3/scoring/speaking`, but nothing in the editor could set it — the value
 * only ever arrived from an AI job or a Moodle import, so an imported
 * qtype_speechace prompt was stuck with whatever the importer chose and an author
 * had no way to correct it. Mirrors Essay's grading-type control.
 *
 * ELMS-2369: speaking is always AI-graded (like essay), so the picker is always shown.
 */
export const SpeakingGradingTypeField: React.FC<SpeakingGradingTypeFieldProps> = ({
    value,
    onChange,
    disabled = false,
    t,
}) => {
    const [exampleOpen, setExampleOpen] = React.useState(false);
    const selectedCode = value ?? DEFAULT_SPEAKING_GRADING_QUESTION_TYPE;
    const selected = SPEAKING_GRADING_QUESTION_TYPES.find((type) => type.code === selectedCode);

    return (
        <div className="speaking-edit-grading space-y-2">
            <div className="speaking-edit-grading-type space-y-2">
                <div className="speaking-edit-grading-type-labelrow flex items-center justify-between gap-3">
                    <Label htmlFor="speaking-grading-type">
                        {t('speakingGradingType')} <span className="text-destructive">*</span>
                    </Label>
                    <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="speaking-edit-grading-type-example-button h-auto p-0"
                        onClick={() => setExampleOpen(true)}
                    >
                        {t('speakingGradingViewExample')}
                    </Button>
                </div>
                <Select value={selectedCode} onValueChange={onChange} disabled={disabled}>
                    <SelectTrigger
                        id="speaking-grading-type"
                        className="speaking-edit-grading-type-trigger"
                    >
                        {/* EAA-1890: the trigger renders its own row instead of
                            `SelectValue`, which would echo the option's stacked markup and
                            centre two lines inside a one-line control. Name left, example
                            right, truncated — the example is context, not the value. */}
                        {selected ? (
                            <span className="speaking-edit-grading-type-value w-full min-w-0 justify-between">
                                <span className="speaking-edit-grading-type-value-name shrink-0">
                                    {selected.name}
                                </span>
                                <span className="speaking-edit-grading-type-value-example min-w-0 flex-1 truncate text-right text-xs text-muted-foreground">
                                    {t(selected.exampleKey)}
                                </span>
                            </span>
                        ) : (
                            <SelectValue placeholder={t('speakingSelectGradingType')} />
                        )}
                    </SelectTrigger>
                    <SelectContent>
                        {/* EAA-1890: each option shows a one-line example under its name.
                            The PTE-style labels ("Retell Lecture", "Respond to a
                            Situation") say nothing about what the learner is asked to do,
                            so the choice was guesswork. */}
                        {SPEAKING_GRADING_QUESTION_TYPES.map((type) => (
                            <SelectItem key={type.code} value={type.code}>
                                <span className="speaking-edit-grading-type-option flex flex-col gap-0.5">
                                    <span className="speaking-edit-grading-type-option-name">
                                        {type.name}
                                    </span>
                                    <span className="speaking-edit-grading-type-option-example text-xs text-muted-foreground">
                                        {t(type.exampleKey)}
                                    </span>
                                </span>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <SpeakingGradingExampleDialog
                open={exampleOpen}
                onOpenChange={setExampleOpen}
                questionType={selectedCode}
                t={t}
            />
        </div>
    );
};
